﻿export interface ISession {
    principal: IPrincipal;
    /**
     * @description returns true if the claim is present, and has a value that can be coerced to boolean true
     * @param claimName case sensitive
     * @returns returns true if the claim is present, and is assigned the value 'true'
     */
    hasClaim(claimName: string): boolean;

    /**
     * @description Will return true if any of the claims passed are present, and have a value that can be coerced to boolean true
     * @param array of claimNames case sensitive
     * @returns true if any of the specified claims are present and are assigned the value 'true'
     */
    hasAny(claimNames: string[]): boolean;
    getClaim(claimName: string): IClaim;
    initializeSession(principal: IPrincipal): void;
    isProvisionalSession: boolean;
}

export interface ICustomerSession extends ISession {
    isCustomerAgreementAccepted: boolean;
}

export class Session implements ICustomerSession {
    public principal: IPrincipal; 
    public isProvisionalSession: boolean;
    private customerAgreementAccepted: boolean;
    private customerAgreementClaimName = 'acceptedterms';

    public static $inject = [];
    constructor() {

    }

    public initializeSession(principal: IPrincipal): void {

        if (angular.isDefined(principal)) {
            this.principal = principal;

        }
    }
    // this is kind of a workaround for when a user first accepts the user agreement.  During the session where they accept the agreeement, 
    // they won't have the claim, so we have this extra "customerAgreementAccepted" internal flag.  
    // In subsequent sessions, the claim will be present.  if you just use the isCustomerAgreementAccepted property, you will always get the right answer
    public get isCustomerAgreementAccepted(): boolean {
        return this.customerAgreementAccepted || this.hasClaim(this.customerAgreementClaimName);
    }

    public set isCustomerAgreementAccepted(value: boolean) {
        this.customerAgreementAccepted = value;
    }

    /**
     * @param claimName case sensitive
     */
    public hasClaim(claimName: string): boolean {
        if (!angular.isDefined(this.principal) || !angular.isDefined(claimName) || !angular.isDefined(this.principal.claims))
            return false;

        // intentional coercion
        return (claimName in this.principal.claims && this.principal.claims[claimName].value === 'true');
    }

    /**
     * @description Accepts array of names of claims, returns true if any are held. 
     * @param claimNames case sensitive
     */
    public hasAny(claimNames: string[]): boolean {
        if (!angular.isDefined(claimNames) || !angular.isDefined(this.principal) || !angular.isDefined(this.principal.claims))
            return false;

        for (const name of claimNames) {
            // intentional coercion
            if (name in this.principal.claims && this.principal.claims[name].value === 'true')
                return true;
        }
        return false;
    }

    /*
     * Returns undefined if the principal does not have the claim
     * @param claimName case sensitive
    */
    public getClaim(claimName: string): IClaim {
        if (!angular.isDefined(claimName) || !angular.isDefined(this.principal) || !angular.isDefined(this.principal.claims))
            return undefined;

        return this.principal.claims[claimName];
    }
}

export interface IPrincipal {
    identity: IIdentity;
    // map of claims, where the key is the claim name and the value is the IClaim
    claims: IClaim[];
}

export interface IClaim {
    name: string;
    value: string;
}

export interface IIdentity {
    userName: string;
    email: string;
}

export interface IClaimsProfile {
    // array of names of claims
    claims: string[];
    allowAnonymousAccess: boolean;
}

export interface IExposeClaimsProfile {
    claimsProfile: IClaimsProfile;
}

// typeguard for IExposeClaimsProfile
export function isIExposeClaimsProfile(data: any): data is IExposeClaimsProfile {
    return (data as IExposeClaimsProfile).claimsProfile !== undefined;
}

export function isIClaimsProfile(data: any): data is IClaimsProfile {
    return ((data as IClaimsProfile).allowAnonymousAccess !== undefined) ||
        ((data as IClaimsProfile).claims !== undefined);
}

// tslint:disable-next-line:max-classes-per-file
export class Principal implements IPrincipal {
    public identity: IIdentity;
    public claims: IClaim[];
}

// tslint:disable-next-line:max-classes-per-file
export class Identity implements IIdentity {
    public userName: string;
    public email: string;
}